import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Download, Search } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/format";

const SOURCE_LABELS = {
  wizard_no_coverage: "Wizard — no coverage",
  quick_quote_no_coverage: "Home — ZIP no coverage",
  zone_landing: "Zone landing",
};

export default function AdminWaitlist() {
  const { locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/waitlist");
        setItems(unwrap(r) || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const stateOptions = useMemo(() => {
    const set = new Set();
    items.forEach((w) => { if (w.state) set.add(w.state); });
    return ["all", ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((w) => {
      if (stateFilter !== "all" && (w.state || "") !== stateFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(w.email || "").toLowerCase().includes(q) &&
            !(w.full_name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [items, stateFilter, search]);

  const downloadCsv = async () => {
    const r = await fetch(`${API_BASE}/admin/waitlist/export.csv`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `everclean-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-waitlist">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Waitlist</h1>
          <p className="text-[#4B5563] text-sm mt-1">{filtered.length} of {items.length} leads from places we don't cover yet</p>
        </div>
        {items.length > 0 && (
          <Button onClick={downloadCsv} variant="outline" className="rounded-xl" data-testid="waitlist-export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3" data-testid="waitlist-filters">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="h-10 rounded-xl pl-9"
              data-testid="waitlist-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#4B5563] font-medium">State:</span>
            <div className="flex flex-wrap gap-1.5">
              {stateOptions.map((s) => (
                <Button
                  key={s}
                  variant={stateFilter === s ? "default" : "outline"}
                  size="sm"
                  className={`rounded-xl h-8 px-3 text-xs ${stateFilter === s ? "bg-[#0EA5A4] hover:bg-[#0F766E] text-white" : ""}`}
                  onClick={() => setStateFilter(s)}
                  data-testid={`waitlist-state-${s}`}
                >
                  {s === "all" ? "All" : s}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#4B5563]" data-testid="waitlist-empty">
            <Mail className="w-7 h-7 mx-auto mb-2 text-[#9CA3AF]" />
            {items.length === 0 ? "No waitlist signups yet." : "No leads match these filters."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="bg-[#FAFAF9] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">ZIP</th>
                  <th className="px-4 py-3 font-semibold">City</th>
                  <th className="px-4 py-3 font-semibold">State</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {filtered.map((w) => (
                  <tr key={w.id} className="hover:bg-[#FAFAF9]" data-testid={`waitlist-${w.id}`}>
                    <td className="px-4 py-3 font-medium">{w.full_name || "—"}</td>
                    <td className="px-4 py-3 truncate max-w-[220px]">{w.email}</td>
                    <td className="px-4 py-3 font-mono text-[#4B5563]">{w.phone || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[#4B5563]">{w.zip_code || "—"}</td>
                    <td className="px-4 py-3">{w.city || "—"}</td>
                    <td className="px-4 py-3 font-mono">{w.state || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">{SOURCE_LABELS[w.source] || w.source || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#9CA3AF] font-mono whitespace-nowrap">{formatDate(w.created_at, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
